import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FileText, Search, Filter, Calendar, User, Image as ImageIcon,
  Send, Download, Trash2, ExternalLink, X, CheckCircle2, Clock, XCircle,
  Plus, Mail,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";

const STATUS_META = {
  draft:     { label: "Rascunho",   color: "bg-[#1a4640] text-[#a0b2af] border-[#2a564f]",       Icon: FileText },
  sent:      { label: "Enviado",    color: "bg-[#3a2f0a] text-[#e8c860] border-[#d4af37]/40",    Icon: Clock },
  signed:    { label: "Assinado",   color: "bg-[#0a3a33] text-[#7fd9c2] border-[#1a8868]",       Icon: CheckCircle2 },
  cancelled: { label: "Cancelado",  color: "bg-red-900/30 text-red-300 border-red-500/40",       Icon: XCircle },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${m.color}`}>
      <Icon size={12} /> {m.label}
    </span>
  );
}


export default function AdminContracts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", image_auth: "", date_from: "", date_to: "" });
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.image_auth) params.image_auth = filters.image_auth;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await api.get("/admin/contracts", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao carregar"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.image_auth, filters.date_from, filters.date_to]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter(c =>
      (c.client?.name || "").toLowerCase().includes(q)
      || (c.client?.email || "").toLowerCase().includes(q)
      || (c.client?.nif || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  async function sendContract(c) {
    const email = window.prompt("Confirme/altere o email do destinatário:", c.sent_to_email || c.client?.email || "");
    if (!email) return;
    try {
      await api.post(`/admin/contracts/${c.id}/send`, { email });
      toast.success("Contrato enviado!");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao enviar"));
    }
  }

  async function cancelContract(c) {
    if (!confirm("Cancelar este contrato? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete(`/admin/contracts/${c.id}`);
      toast.success("Contrato cancelado.");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao cancelar"));
    }
  }

  function copySignLink(c) {
    if (!c.token) {
      toast.error("Envie o contrato primeiro para gerar o link.");
      return;
    }
    const url = `${window.location.origin}/contratos/assinar/${c.token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  }

  return (
    <div className="space-y-6" data-testid="admin-contracts-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#fdfbf7]">
            Contratos <span className="italic text-[#d4af37]">digitais</span>
          </h1>
          <p className="mt-1 text-sm text-[#a0b2af]">
            Gestão e revisão dos contratos enviados aos clientes.
          </p>
        </div>
        <button onClick={() => setCreateOpen(true)}
                data-testid="contract-new-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-xs font-semibold uppercase tracking-widest bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all">
          <Plus size={14} /> Novo contrato
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-[#1a4640] bg-[#062c27] p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a0b2af]" />
            <input data-testid="contract-search" type="search"
                   value={search} onChange={e => setSearch(e.target.value)}
                   placeholder="Pesquisar por cliente, email, NIF…"
                   className="w-full pl-9 pr-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] placeholder:text-[#a0b2af] focus:border-[#d4af37] focus:outline-none" />
          </div>
          <select value={filters.status}
                  onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                  data-testid="contract-filter-status"
                  className="px-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none">
            <option value="">Todos os estados</option>
            <option value="draft">Rascunho</option>
            <option value="sent">Enviado</option>
            <option value="signed">Assinado</option>
            <option value="cancelled">Cancelado</option>
          </select>
          <select value={filters.image_auth}
                  onChange={e => setFilters(f => ({ ...f, image_auth: e.target.value }))}
                  data-testid="contract-filter-image"
                  className="px-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none">
            <option value="">Imagens (todos)</option>
            <option value="yes">Autoriza</option>
            <option value="no">Não autoriza</option>
          </select>
          <input type="date" value={filters.date_from}
                 onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
                 className="px-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none" />
          <input type="date" value={filters.date_to}
                 onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
                 className="px-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none" />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map(i => <div key={i} className="h-20 rounded-xl border border-[#1a4640] bg-[#062c27] animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#1a4640] bg-[#062c27] py-16 text-center"
             data-testid="contracts-empty">
          <Filter size={32} className="mx-auto text-[#1a4640]" />
          <p className="mt-4 text-sm text-[#a0b2af]">
            Sem contratos correspondentes. Clique em «Novo contrato» para começar.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-[#1a4640] bg-[#062c27] overflow-hidden">
          <ul className="divide-y divide-[#1a4640]">
            {filtered.map(c => (
              <li key={c.id} data-testid={`contract-row-${c.id}`}
                  className="p-4 sm:p-5 hover:bg-[#0a3a33]/30 transition-colors">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-base text-[#fdfbf7] truncate">
                        {c.client?.name || "Cliente sem nome"}
                      </h3>
                      <StatusBadge status={c.status} />
                      {c.image_authorization && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${
                          c.image_authorization === "yes"
                            ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                            : "bg-red-500/10 text-red-300 border-red-500/40"
                        }`}>
                          <ImageIcon size={10} /> {c.image_authorization === "yes" ? "Autoriza" : "Não autoriza"}
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 text-xs text-[#a0b2af] flex flex-wrap items-center gap-3">
                      <span><User size={11} className="inline mr-1" />{c.client?.email || "—"}</span>
                      <span><Calendar size={11} className="inline mr-1" />{fmt(c.created_at)}</span>
                      <span>{(c.properties || []).filter(p => p.included).length} propriedades · {c.global_scope ? "global" : "específicas"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {c.status !== "signed" && c.status !== "cancelled" && (
                      <button onClick={() => sendContract(c)}
                              data-testid={`contract-send-${c.id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] transition-all">
                        <Send size={11} /> Enviar
                      </button>
                    )}
                    {c.token && c.status === "sent" && (
                      <button onClick={() => copySignLink(c)}
                              data-testid={`contract-copy-${c.id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold uppercase tracking-wider border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 transition-all">
                        <ExternalLink size={11} /> Copiar link
                      </button>
                    )}
                    {c.status === "signed" && (
                      <a href={`${BACKEND_URL}/api/admin/contracts/${c.id}/pdf`}
                         target="_blank" rel="noopener noreferrer"
                         data-testid={`contract-pdf-${c.id}`}
                         className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold uppercase tracking-wider border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 transition-all">
                        <Download size={11} /> PDF
                      </a>
                    )}
                    {c.status !== "signed" && (
                      <button onClick={() => cancelContract(c)}
                              data-testid={`contract-cancel-${c.id}`}
                              className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {createOpen && (
        <NewContractModal onClose={() => { setCreateOpen(false); load(); }} />
      )}
    </div>
  );
}


function NewContractModal({ onClose }) {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Try the standard admin clients listing
        const { data } = await api.get("/admin/clients");
        const list = Array.isArray(data) ? data : (data.items || data.users || []);
        setClients(list);
      } catch {
        try {
          const { data } = await api.get("/admin/users", { params: { role: "client" } });
          const list = Array.isArray(data) ? data : (data.items || data.users || []);
          setClients(list);
        } catch (e) {
          toast.error(formatApiError(e, "Erro ao carregar clientes"));
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return clients;
    const q = search.trim().toLowerCase();
    return clients.filter(c =>
      (c.name || "").toLowerCase().includes(q)
      || (c.email || "").toLowerCase().includes(q)
    );
  }, [clients, search]);

  async function pick(client, send) {
    setSubmitting(true);
    try {
      const { data: created } = await api.post("/admin/contracts", { client_id: client.id });
      if (send) {
        await api.post(`/admin/contracts/${created.id}/send`, { email: client.email });
        toast.success(`Contrato enviado para ${client.email}`);
      } else {
        toast.success("Rascunho criado.");
      }
      onClose();
    } catch (e) {
      toast.error(formatApiError(e, "Erro"));
    } finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="contract-new-modal">
      <div className="relative bg-[#062c27] border border-[#d4af37]/40 rounded-2xl p-6 sm:p-8 max-w-lg w-full max-h-[80vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <button onClick={onClose}
                className="absolute top-3 right-3 p-2 rounded-full text-[#a0b2af] hover:text-[#fdfbf7] hover:bg-[#1a4640]/50 transition-colors">
          <X size={18} />
        </button>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#d4af37]">Novo contrato</div>
        <h3 className="mt-2 font-display text-xl text-[#fdfbf7]">Selecione o cliente</h3>

        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Pesquisar por nome ou email…"
               data-testid="contract-new-search"
               className="mt-5 w-full px-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none" />

        {loading ? (
          <p className="mt-6 text-sm text-[#a0b2af]">A carregar clientes…</p>
        ) : filtered.length === 0 ? (
          <p className="mt-6 text-sm text-[#a0b2af]">Sem resultados.</p>
        ) : (
          <ul className="mt-4 space-y-2 max-h-[40vh] overflow-y-auto">
            {filtered.map(c => (
              <li key={c.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-md border border-[#1a4640] bg-[#041f1b] hover:border-[#d4af37]/50 transition-colors">
                <div className="min-w-0">
                  <div className="text-sm text-[#fdfbf7] truncate">{c.name || "Sem nome"}</div>
                  <div className="text-xs text-[#a0b2af] truncate">{c.email || "—"}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => pick(c, false)}
                          disabled={submitting}
                          data-testid={`contract-pick-draft-${c.id}`}
                          className="px-3 py-1.5 rounded-md text-[11px] uppercase tracking-wider border border-[#1a4640] text-[#a0b2af] hover:text-[#d4af37] transition-all disabled:opacity-40">
                    Rascunho
                  </button>
                  <button onClick={() => pick(c, true)}
                          disabled={submitting || !c.email}
                          data-testid={`contract-pick-send-${c.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] transition-all disabled:opacity-40">
                    <Mail size={10} /> Criar &amp; Enviar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
